<template>
  <section :class="$style.component">
    <div :class="$style.slider">
      <div :class="$style.slide">
        <transition :name="transitionName">
          <image-slide
            :image="currentItem"
            :class="$style.image"
            :key="`slide-${currentSlide}`"
          />
        </transition>
      </div>
      <transition name="t-info" mode="out-in">
        <image-slide-description
          :image="currentItem"
          :subtitle="subtitle"
          :key="`info-${currentSlide}`"
          :class="$style.description"
        />
      </transition>
    </div>
    <button v-if="!bioVisible" :class="classes.prev" @click="prev" />
    <button v-if="!bioVisible" :class="classes.next" @click="next" />
  </section>
</template>

<script>
import ImageSlide from '@/components/image-slide.vue'
import ImageSlideDescription from '@/components/image-slide-description'
import { mapState } from 'vuex'

export default {
  components: {
    ImageSlide,
    ImageSlideDescription
  },
  props: {
    media: {
      type: Array,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      currentSlide: 0,
      onEnteredSlide: true
    }
  },
  computed: {
    ...mapState(['bioVisible']),
    classes() {
      return {
        prev: [
          this.$style.prev,
          this.slideCount > 1 ? this.$style['is-visible'] : ''
        ],
        next: [
          this.$style.next,
          this.slideCount > 1 ? this.$style['is-visible'] : ''
        ]
      }
    },
    slideCount() {
      return this.media.length
    },
    firstSlide() {
      return this.currentSlide === 0
    },
    lastSlide() {
      return this.currentSlide === this.slideCount - 1
    },
    currentItem() {
      return this.media[this.currentSlide]
    },
    transitionName() {
      return this.onEnteredSlide ? 't-none' : 't-slide'
    }
  },
  methods: {
    prev() {
      if (this.firstSlide) {
        this.currentSlide = this.slideCount - 1
      } else {
        this.currentSlide--
      }
      this.onEnteredSlide = false
    },
    next() {
      this.currentSlide = (this.currentSlide + 1) % this.slideCount
      this.onEnteredSlide = false
    },
    recallQuery() {
      // NOTE: Check for undefined because if we just check for slide, then 0 would also return
      if (this.$route.query.slide === undefined) return
      this.currentSlide = parseInt(this.$route.query.slide)
    },
    // Set slide count in store to use it in artist-header
    setSlideAmount() {
      this.$store.commit('setSlideAmount', {
        slideAmount: this.slideCount
      })
    },
    unsetSlideAmount() {
      this.$store.commit('setSlideAmount', {
        slideAmount: 0
      })
    },
    // Set current slide in store to use it in artist-header
    setCurrentSlide() {
      this.$store.commit('setCurrentSlide', {
        currentSlide: this.currentSlide
      })
    },
    unsetCurrentSlide() {
      this.$store.commit('setCurrentSlide', {
        currentSlide: 0
      })
    },
    // Update query when current slide changes
    updateQuery() {
      this.$router.replace({
        query: { slide: this.currentSlide }
      })
    }
  },
  created() {
    this.recallQuery()
    this.setSlideAmount()
  },
  activated() {
    this.recallQuery()
    this.setSlideAmount()
  },
  deactivated() {
    this.onEnteredSlide = true
    this.unsetSlideAmount()
    this.unsetCurrentSlide()
  },
  watch: {
    currentSlide() {
      this.updateQuery()
      this.setCurrentSlide()
    }
  }
}
</script>

<style lang="scss" scoped>
$duration: var(--xlong);
@include t-fade($name: 't-info', $duration: var(--long), $easing: ease);

.t-slide-enter-active {
  transition: opacity $duration ease,
    transform $duration var(--ease-in-out-back);
}

.t-slide-leave-active {
  transition: opacity $duration ease, transform var(--xxlong) ease;
}

.t-slide-enter {
  opacity: 0;
  transform: scale(0.2);
}

.t-slide-enter-to,
.t-slide-leave {
  opacity: 1;
  transform: scale(1);
}

.t-slide-leave-to {
  opacity: 0;
  transform: scale(2.5);
}
</style>

<style lang="scss" module>
.component {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.prev,
.next {
  @extend %tap-highlight-reset;
  display: none;
  position: absolute;
  z-index: 1;
  top: 0;
  width: 50%;
  height: 100%;
  outline: 0;

  &.is-visible {
    display: block;
  }
}

.prev {
  left: 0;
  cursor: url('@/assets/images/arrow-left.png') 13 24, w-resize;
  cursor: image-set(
        url('@/assets/images/arrow-left.png') 1x,
        url('@/assets/images/arrow-left@2x.png') 2x
      )
      13 24,
    w-resize;
}

.next {
  right: 0;
  cursor: url('@/assets/images/arrow-right.png') 13 24, e-resize;
  cursor: image-set(
        url('@/assets/images/arrow-right.png') 1x,
        url('@/assets/images/arrow-right@2x.png') 2x
      )
      13 24,
    e-resize;
}

.slider {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding-top: var(--slide-padding);
  padding-right: var(--gutter);
  padding-left: var(--gutter);
}

.slide {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
}

.image {
  position: absolute;
  width: 100%;
  height: 100%;
}

.description {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  // padding: var(--gutter) 0;
  // min-height: var(--slide-padding);
  padding-bottom: var(--gutter);
  height: var(--slide-padding);
}
</style>
